import * as React from 'react';
import { ChevronDown, ChevronRight, Copy, Check, Filter } from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { LogEntry, LogLevel } from '../lib/api.js';

/**
 * Render text with newlines as <br/> elements
 */
function renderWithNewlines(text: string): React.ReactNode {
  const parts = text.split('\n');
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && <br />}
    </React.Fragment>
  ));
}
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.js';
import { Badge } from './ui/badge.js';
import { CodeBlock } from './ui/codeBlock.js';
import { ErrorContextViewer, isErrorContext } from './ErrorContextViewer.js';

/**
 * ID Badge with copy and filter functionality
 */
interface IdBadgeProps {
  label: string;
  value: string;
  onFilter?: () => void;
  variant: 'instance' | 'session' | 'correlation';
}

function IdBadge({ label, value, onFilter, variant }: IdBadgeProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleFilter = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFilter?.();
  };

  const variantStyles = {
    instance: {
      bg: 'bg-amber-500/10',
      text: 'text-amber-600 dark:text-amber-400',
      border: 'border-amber-500/20',
      hoverBg: 'hover:bg-amber-500/20',
      hoverBorder: 'hover:border-amber-500/30',
    },
    session: {
      bg: 'bg-[var(--color-primary)]/10',
      text: 'text-[var(--color-primary)]',
      border: 'border-[var(--color-primary)]/20',
      hoverBg: 'hover:bg-[var(--color-primary)]/20',
      hoverBorder: 'hover:border-[var(--color-primary)]/30',
    },
    correlation: {
      bg: 'bg-[var(--color-muted)]',
      text: 'text-[var(--color-muted-foreground)]',
      border: 'border-[var(--color-border)]',
      hoverBg: 'hover:bg-[var(--color-muted)]/80',
      hoverBorder: 'hover:border-[var(--color-primary)]/30',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-md text-xs font-medium',
        styles.bg,
        styles.text,
        'border',
        styles.border
      )}
    >
      <span className="pl-2.5 py-1 opacity-70">{label}:</span>
      <span className="font-mono py-1">{value}</span>
      <div className="flex items-center border-l border-current/20">
        <button
          onClick={handleCopy}
          className={cn(
            'p-1.5 transition-colors',
            'hover:bg-black/10 dark:hover:bg-white/10'
          )}
          title={copied ? 'Copied!' : 'Copy to clipboard'}
        >
          {copied ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
        {onFilter && (
          <button
            onClick={handleFilter}
            className={cn(
              'p-1.5 pr-2 transition-colors',
              'hover:bg-black/10 dark:hover:bg-white/10'
            )}
            title="Filter by this value"
          >
            <Filter className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Map log levels to badge variants
 */
const levelVariant: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error' | 'fatal'> = {
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
  fatal: 'fatal',
};

export interface LogTableProps {
  /** Array of log entries to display */
  logs: LogEntry[];
  /** Currently expanded row ID */
  expandedId?: string | null;
  /** Callback when a row is clicked */
  onRowClick?: (log: LogEntry) => void;
  /** Callback when user clicks to filter by session ID */
  onFilterSession?: (sessionId: string) => void;
  /** Callback when user clicks to filter by correlation ID */
  onFilterCorrelation?: (correlationId: string) => void;
  /** Callback when user clicks to filter by instance ID */
  onFilterInstance?: (instanceId: string) => void;
  /** Additional class name for the table container */
  className?: string;
  /** Whether the table is in a loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
}

/**
 * LogTable displays log entries in a table format with expandable context.
 */
export function LogTable({
  logs,
  expandedId,
  onRowClick,
  onFilterSession,
  onFilterCorrelation,
  onFilterInstance,
  className,
  isLoading = false,
  emptyMessage = 'No logs found',
}: LogTableProps) {
  const [internalExpandedId, setInternalExpandedId] = React.useState<string | null>(null);

  // Use controlled or uncontrolled expanded state
  const currentExpandedId = expandedId !== undefined ? expandedId : internalExpandedId;

  const handleRowClick = (log: LogEntry) => {
    if (onRowClick) {
      onRowClick(log);
    } else {
      setInternalExpandedId(currentExpandedId === log.id ? null : log.id);
    }
  };

  const formatTimestamp = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatContext = (context: Record<string, unknown> | null) => {
    if (!context) return null;
    try {
      return JSON.stringify(context, null, 2);
    } catch {
      return String(context);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <div className="flex flex-col items-center gap-3 text-[var(--color-muted-foreground)]">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-primary)] border-t-transparent" />
          <span className="text-sm">Loading logs...</span>
        </div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <p className="text-[var(--color-muted-foreground)]">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn('overflow-hidden rounded-lg border border-[var(--color-border)]', className)}>
      <Table className="table-fixed w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead className="w-44">Timestamp</TableHead>
            <TableHead className="w-20">Level</TableHead>
            <TableHead className="w-32">Sub App</TableHead>
            <TableHead className="w-auto">Message</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const isExpanded = currentExpandedId === log.id;
            const hasContext = log.context && Object.keys(log.context).length > 0;

            return (
              <React.Fragment key={log.id}>
                <TableRow
                  className={cn(
                    'cursor-pointer',
                    isExpanded && 'bg-[var(--color-primary)]/5'
                  )}
                  onClick={() => handleRowClick(log)}
                >
                  <TableCell className="py-2">
                    {hasContext ? (
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                      )
                    ) : null}
                  </TableCell>
                  <TableCell className="py-2 font-mono text-xs text-[var(--color-muted-foreground)]">
                    {formatTimestamp(log.ts)}
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge variant={levelVariant[log.level]}>
                      {log.level.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2 text-sm text-[var(--color-muted-foreground)]">
                    {log.sub_app || '-'}
                  </TableCell>
                  <TableCell className="py-2 overflow-hidden">
                    <span className="line-clamp-2 text-sm text-[var(--color-foreground)] break-all whitespace-pre-wrap">{renderWithNewlines(log.message)}</span>
                  </TableCell>
                </TableRow>
                {isExpanded && hasContext && (
                  <TableRow>
                    <TableCell colSpan={5} className="bg-[var(--color-secondary)]/50 p-4 overflow-hidden">
                      <div className="space-y-3 overflow-hidden">
                        {/* Log ID and Session/Correlation/Instance ID badges */}
                        <div className="flex flex-wrap gap-2">
                          {/* Log ID - always show */}
                          <IdBadge
                            label="Log ID"
                            value={log.id}
                            variant="correlation"
                          />
                          {log.instance_id && (
                            <IdBadge
                              label="Instance"
                              value={log.instance_id}
                              variant="instance"
                              onFilter={() => onFilterInstance?.(log.instance_id!)}
                            />
                          )}
                          {log.session_id && (
                            <IdBadge
                              label="Session"
                              value={log.session_id}
                              variant="session"
                              onFilter={() => onFilterSession?.(log.session_id!)}
                            />
                          )}
                          {log.correlation_id && (
                            <IdBadge
                              label="Correlation"
                              value={log.correlation_id}
                              variant="correlation"
                              onFilter={() => onFilterCorrelation?.(log.correlation_id!)}
                            />
                          )}
                        </div>
                        {/* Context - use ErrorContextViewer for error tracking data */}
                        {log.context && isErrorContext(log.context) ? (
                          <ErrorContextViewer
                            context={log.context as import('./ErrorContextViewer.js').ErrorContext}
                            onFilterSession={onFilterSession}
                          />
                        ) : (
                          <div className="space-y-2">
                            <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                              Context
                            </span>
                            <CodeBlock>{formatContext(log.context) ?? ''}</CodeBlock>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default LogTable;
